import { useMemo } from 'react';
import { Add, DeleteOutline } from '@mui/icons-material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import uuid from 'react-uuid';
import {
  DAILY_NAV_MONITORING_MODE_OPTIONS,
  DAILY_NAV_TRADING_MARKET_OPTIONS,
  dailyNavMarketTimezone,
  DailyNavMonitoringMode,
  isValidReportSendTime,
  parseDailyNavReportSendTime,
  REPORT_SEND_TIME_MINUTE_STEP,
  snapDailyNavReportSendTime,
} from 'common/constants/dailyNav';
import { Box, Button, Grid, IconButton, Input, MenuItem, Stack, Text } from 'components/ui';
import { DAILY_NAV_SETUP_TEST_IDS } from '../DailyNavSetup.testIds';
import { FirmStepProps } from './FirmStep.props';
import { editRecipientRows, isValidEmail } from '../hooks/useFirmSetupForm';

const FirmMarketStep = ({ form, disabled }: FirmStepProps) => {
  const { draft, update } = form;

  // A stored value could predate the curated list; keep it selectable.
  const marketOptions = useMemo(() => {
    if (!draft.tradingMarket || DAILY_NAV_TRADING_MARKET_OPTIONS.some(option => option.value === draft.tradingMarket)) {
      return DAILY_NAV_TRADING_MARKET_OPTIONS;
    }
    return [...DAILY_NAV_TRADING_MARKET_OPTIONS, { value: draft.tradingMarket, label: draft.tradingMarket }];
  }, [draft.tradingMarket]);

  const timezone = dailyNavMarketTimezone(draft.tradingMarket);

  return (
    <Stack spacing={4}>
      <div>
        <Text variant="h4" gutterBottom>
          Which market defines the NAV day?
        </Text>
        <Text variant="body2" sx={{ mb: 2 }}>
          Each NAV day closes when this market closes. Market data is read up to that moment, or until the Daily NAV is
          finalized. Holidays are skipped and early closes are handled for you.
        </Text>
        <Text variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This only sets timing. Which comps are tracked is chosen per company later in this setup.
        </Text>
        <Grid container>
          <Grid size={{ xs: 12, sm: 7 }}>
            <Input
              select
              fullWidth
              size="small"
              id="daily-nav-setup-trading-market"
              label="Trading market"
              value={draft.tradingMarket}
              onChange={event => update('tradingMarket', event.target.value)}
              error={!draft.tradingMarket}
              disabled={disabled}>
              {marketOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Input>
          </Grid>
        </Grid>
      </div>
      <div>
        <Text variant="h4" gutterBottom sx={{ mb: 2 }}>
          Who reviews the daily grid?
        </Text>
        <Grid container>
          <Grid size={{ xs: 12, sm: 7 }}>
            <Input
              select
              fullWidth
              size="small"
              id="daily-nav-setup-monitoring-mode"
              label="Monitored by"
              value={draft.monitoringMode}
              onChange={event => update('monitoringMode', event.target.value as DailyNavMonitoringMode)}
              disabled={disabled}>
              {DAILY_NAV_MONITORING_MODE_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Input>
          </Grid>
        </Grid>
      </div>
      <div>
        <Text variant="h4" gutterBottom>
          When should the daily reminder email go out?
        </Text>
        <Text variant="body2" sx={{ mb: 2 }}>
          A clock time in the market&apos;s timezone ({timezone}), not UTC or your local time.
        </Text>
        <Grid container>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TimePicker
              ampm={false}
              format="HH:mm"
              views={['hours', 'minutes']}
              label={`Reminder time (${timezone})`}
              value={parseDailyNavReportSendTime(draft.reportSendTime)}
              onChange={value => {
                if (!value || Number.isNaN(value.getTime())) {
                  return;
                }
                update('reportSendTime', snapDailyNavReportSendTime(value));
              }}
              timeSteps={{ minutes: REPORT_SEND_TIME_MINUTE_STEP }}
              minutesStep={REPORT_SEND_TIME_MINUTE_STEP}
              skipDisabled
              shouldDisableTime={(value, view) =>
                view === 'minutes' && value.getMinutes() % REPORT_SEND_TIME_MINUTE_STEP !== 0
              }
              disabled={disabled}
              slotProps={{
                textField: {
                  id: DAILY_NAV_SETUP_TEST_IDS.reportSendTime,
                  size: 'small',
                  fullWidth: true,
                  error: !isValidReportSendTime(draft.reportSendTime),
                },
              }}
            />
          </Grid>
        </Grid>
      </div>
      <div>
        <Text variant="h4" gutterBottom>
          Who gets the daily reminder email?
        </Text>
        <Text variant="body2" sx={{ mb: 2 }}>
          These people get a reminder at the time above, listing each company&apos;s review status. You can leave this
          empty and add people later.
        </Text>
        <Stack spacing={2}>
          {draft.recipients.map((recipient, index) => (
            <Grid container spacing={2} key={recipient.key} alignItems="center">
              <Grid size={{ xs: 10, sm: 6 }}>
                <Input
                  fullWidth
                  size="small"
                  type="email"
                  placeholder="e.g. alerts@firm.com"
                  data-testid={DAILY_NAV_SETUP_TEST_IDS.recipient(index)}
                  value={recipient.email}
                  onChange={event => update('recipients', editRecipientRows(draft.recipients, index, event.target.value))}
                  error={Boolean(recipient.email) && !isValidEmail(recipient.email)}
                  disabled={disabled}
                />
              </Grid>
              <Grid size={{ xs: 2, sm: 1 }}>
                <IconButton
                  color="error"
                  aria-label="Remove recipient"
                  data-testid={DAILY_NAV_SETUP_TEST_IDS.removeRecipientButton(index)}
                  onClick={() => update('recipients', draft.recipients.filter((_, rowIndex) => rowIndex !== index))}
                  disabled={disabled}>
                  <DeleteOutline />
                </IconButton>
              </Grid>
            </Grid>
          ))}
        </Stack>
        <Box sx={{ mt: 1 }}>
          <Button
            startIcon={<Add />}
            variant="text"
            id={DAILY_NAV_SETUP_TEST_IDS.addRecipient}
            onClick={() => update('recipients', [...draft.recipients, { email: '', key: uuid() }])}
            disabled={disabled}>
            Add recipient
          </Button>
        </Box>
      </div>
    </Stack>
  );
};

export default FirmMarketStep;
